import React, { useEffect, useRef, useState } from 'react';
import { Button, Header, Input, Pagination, Table, Modal } from 'react-frontier2';
import { APIResponse, SetLoading } from '@arema/components/Classes';
import classNames from 'classnames';

interface SearchModalProps<T=any> extends SearchCoreProps<T>{
	searchData: (query: string, page: number, page_size: number)=>Promise<APIResponse<T[]>>,
	onShowLoad?: (query: string, page: number, page_size: number)=>Promise<APIResponse<T[]>>,
	table: {
		name: any,
		key?: keyof T,
		collapsing?: boolean,
		centered?: boolean,
		render?: (val: any, obj: T)=>any
	}[],
}

export interface SearchCoreProps<T>{
	open: boolean,
	onClose: ()=>void,
	search?: boolean,
	title?: string,
	pageSize?: number,
	searchPlaceholder?: string,
	onSelected: (item: T, setLoading: SetLoading)=>void,
}

var SearchCoreModal = (props: SearchModalProps)=>{
	var [searching, setSearching] = useState<boolean>(false);
	var [searchInput, setSearchInput] = useState<string>('');
	var [results, setResults] = useState<any[]>(null);
	var [resultsError, setResultsError] = useState<string>(null);
	var [searchResults, setSearchResults] = useState<any[]>(null);
	var [searchError, setSearchError] = useState<string>(null);
	var [sentQuery, setSentQuery] = useState<string>(null);
	var [page, setPage] = useState<number>(0);
	var [loading, setLoading] = useState<boolean>(false);
	var inputRef = useRef<HTMLInputElement>(null);
	const PAGE_SIZE = (props.pageSize || 15);

	useEffect(()=>{
		if(props.open){
			setSearchInput('');
			inputRef.current?.focus();
			setLoading(false);
			if(props.onShowLoad){
				setSearching(true);
				setResultsError(null);
				setSearchError(null);
				setSentQuery(null);
				props.onShowLoad(null, 0, PAGE_SIZE).then(res=>{
					if(res.error) return setResultsError(res.message);
					setResults(res.data && res.data.length==0 ? null : res.data);
				}).catch(err=>{
					setResults(null);
					// setResultsError('Hubo un error inesperado cargando los datos (LCL-SCHCRE-1)');
				}).finally(()=>{
					setSearching(false);
				})
			}else{
				setSearching(false);
			}
		}
	}, [props.open]);

	var getIndexes = (arr: boolean[])=>{
		var ix : number[] = [];
		for(var i=0; i<arr.length; i++){
			if(!!arr[i]) ix.push(i);
		}
		return ix;
	}

	var searchData = (setLoading: SetLoading, page?: number)=>{
		if(searching) return;
		setSearching(true);
		setSentQuery(searchInput);
		setPage(page || 0);
		setSearchError(null);
		setResultsError(null);
		// if(setLoading) setLoading(true);
		props.searchData(searchInput, page, PAGE_SIZE).then(res=>{
			if(res.error) return setSearchError(res.message);
			setSearchResults(res.data);
		}).catch(err=>{
			setSearchError('Hubo un error inesperado cargando los datos (LCL-SCHCRE-1)');
		}).finally(()=>{
			setSearching(false);
			// if(setLoading) setLoading(false);
		})
	}

	var itemSelected = (item: any)=>{
		return ()=>{
			if(props.onSelected){
				props.onSelected(item, setLoading);
			}
		}
	}

	var onPageChange = (page: number)=>{
		setPage(page);
		searchData(null, page);
	}

	
	var hasPagination = (searchResults?.length || 0) >= PAGE_SIZE || page != 0;
	var shown_results = (searchResults || results);

	return <Modal open={props.open} onClose={props.onClose} size='tiny'>
		<Modal.Header>{props.title}</Modal.Header>
		<Modal.Content style={{ paddingBottom: hasPagination ? 0 : 20, overflow: 'hidden', minHeight: 300 }}>
			{props.search!==false && !!props.open && (
				<Input ref={inputRef} value={searchInput} onChange={setSearchInput} placeholder={props.searchPlaceholder || 'Buscar registros'} inputType='text' buttons={(
					<Button text='Buscar' onClick={searchData} loading={searching} />
				)} onSubmit={searchData} iconName='search' />
			)}
			{searching ? (
				<Header loading text={`Cargando registros`} style={{ marginBottom: 50, marginTop: 50 }} />
			) : (searchError || resultsError) ? (
				<Header text='Error' subtext={resultsError} iconName='face-frown-open' style={{ marginTop: 30, marginBottom: 30 }} size='small' />
			) : !shown_results ? (
				null
			) : (shown_results.length === 0 && page<=0) ? (
				<Header text='Sin resultados' subtext={'No se encontraron registros'+(sentQuery ? ` con "${sentQuery}"` : '')} iconName='face-frown-open' style={{ marginTop: 30, marginBottom: 30 }} size='small' />
			) : <>
				<div style={{ position: 'relative' }}>
					<Table fitted selectable striped divided last removeBorder={['top']}
						style={{ marginTop: props.search===false ? -20 : 20 }}
						className={classNames('last', {
							first: props.search!==false
						})}
					>
						<Table.Head
							data={[props.table.map(a=>a.name)]}
							centeredIndexes={getIndexes(props.table.map(a=>a.centered))}
							collapsingIndexes={getIndexes(props.table.map(a=>a.collapsing))}
						/>
						<Table.Body>
							{(shown_results.length === 0) ? (
								<Table.Cell row colSpan={100}>
									<Header text='Sin resultados' subtext={`Esta página no tiene resultados`} iconName='face-frown-open' style={{ marginTop: 30, marginBottom: 30 }} size='small' />
								</Table.Cell>
							) : (
								shown_results.map((a, i)=>(
									<Table.Row key={`SRCH-CRTR-${i}`} onClick={itemSelected(a)}>
										{props.table.map(t=>(
											<Table.Cell collapsing={t.collapsing} centered={t.centered} key={`SRCH-CRTD-${i}-${t.key as string}`}>
												{t.render ? t.render(a[t.key], a) : a[t.key]}
											</Table.Cell>
										))}
									</Table.Row>
								))
							)}
						</Table.Body>
						{hasPagination && (
							<Table.Footer>
								<Table.Cell row colSpan={100}>
									<div style={{ justifyContent: 'flex-end', display: 'flex' }}>
										<Pagination page={page} disabled={searching} onPageChange={onPageChange} hasNext={shown_results.length >= PAGE_SIZE} />
									</div>
								</Table.Cell>
							</Table.Footer>
						)}
					</Table>
				</div>
			</>}
			{loading && (
				<div style={{ position: 'absolute', width: '100%', left: 0, top: 0, height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)', borderRadius: 8 }} className='fr big loading'></div>
			)}
		</Modal.Content>
	</Modal>
}

export default SearchCoreModal;